import React from 'react';
import { Typography, Grid, Card, Button, Box, Alert } from '@mui/material';
import { WEB_MAIN_COLOR, WEB_SECONDORY_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import { colors } from '../components/Theme/WebTheme';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import GoBackButton from 'components/GoBackButton';
import { api } from 'common';
import { hasPendingBankChange, getPendingBankDetails, getApprovedBankDetails } from 'common/src/other/bankDetailsHelper';

const BankValueCard = ({ label, value, mainColor, secondaryColor, hiddenText }) => (
  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 10, marginBottom: 10 }}
  >
    <Card style={{
      borderRadius: '10px',
      backgroundColor: colors.WHITE,
      minHeight: 70,
      minWidth: 300,
      width: '80%',
      display: 'flex',
      boxShadow: `0px 2px 5px ${secondaryColor}`,
    }}>
      <Typography style={{
        fontSize: 20,
        backgroundColor: mainColor,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
        wordBreak: 'break-word',
        padding: '5px',
        fontFamily: FONT_FAMILY,
        color: colors.WHITE,
      }}>
        {label}
      </Typography>
      <Typography style={{
        fontSize: 18,
        fontWeight: 'bold',
        color: colors.BLACK,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        wordBreak: 'break-word',
        padding: '5px',
        fontFamily: FONT_FAMILY,
      }}>
        {value}
      </Typography>
    </Card>
  </Grid>
);

export const BankDetails = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const { state } = useLocation();
  const { approvePendingBankDetails, rejectPendingBankDetails } = api;
  const MAIN_COLOR = WEB_MAIN_COLOR(settings?.appCat);
  const SECONDORY_COLOR = WEB_SECONDORY_COLOR(settings?.appCat);
  const data = props.data;
  const allowView = !!settings?.AllowCriticalEditsAdmin;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const pending = getPendingBankDetails(data);
  const approved = getApprovedBankDetails(data);
  const showPending = hasPendingBankChange(data);

  const displayValue = (val) => (val ? (allowView ? val : t('hidden_demo')) : t('no_data_available'));

  const goBack = () => {
    if (data?.usertype === 'customer') {
      navigate('/users/0', { state: { pageNo: state?.pageNo } });
    } else if (data?.usertype === 'driver') {
      navigate('/users/1', { state: { pageNo: state?.pageNo } });
    } else {
      navigate('/404');
    }
  };

  const handleApprove = () => {
    if (!data?.id) return;
    dispatch(approvePendingBankDetails(data.id, data));
  };

  const handleReject = () => {
    if (!data?.id) return;
    dispatch(rejectPendingBankDetails(data.id, data));
  };

  return (
    <Card style={{
      borderRadius: '19px',
      backgroundColor: colors.WHITE,
      marginTop: 20,
      marginBottom: 20,
      boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
      paddingBottom: 30,
    }}>
      <GoBackButton isRTL={isRTL} onClick={goBack} />

      {showPending ?
        <Box sx={{ px: 3, pt: 1 }}>
          <Alert severity="warning" sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
            {t('bank_pending_admin_alert', {
              defaultValue: 'Este usuario solicitó un cambio de datos bancarios. Revisa que la cuenta esté a nombre del titular antes de aprobar.',
            })}
          </Alert>
          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, mb: 1 }}>
            {t('bank_pending_review', { defaultValue: 'Cambio solicitado (en revisión)' })}
          </Typography>
          {pending?.holderName ?
            <Typography sx={{ fontFamily: FONT_FAMILY, mb: 0.5 }}>
              {t('bank_account_holder', { defaultValue: 'Titular' })}: {pending.holderName}
              {pending.documentId ? ` · ${pending.documentType || 'CC'} ${pending.documentId}` : ''}
            </Typography>
            : null}
          <Typography sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
            {pending?.bankName} · {pending?.bankAccountType || 'Ahorros'} · {allowView ? pending?.bankAccount : t('hidden_demo')}
          </Typography>
          {isAdmin ?
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button variant="contained" color="success" onClick={handleApprove} sx={{ fontFamily: FONT_FAMILY }}>
                {t('approve_bank_change', { defaultValue: 'Aprobar cambio bancario' })}
              </Button>
              <Button variant="outlined" color="error" onClick={handleReject} sx={{ fontFamily: FONT_FAMILY }}>
                {t('reject_bank_change', { defaultValue: 'Rechazar cambio' })}
              </Button>
            </Box>
            : null}
        </Box>
        : null}

      {approved ?
        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, px: 3, pt: showPending ? 0 : 1, pb: 1 }}>
          {t('bank_current_approved', { defaultValue: 'Cuenta aprobada actual' })}
        </Typography>
        : null}

      <Grid container spacing={1} sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
      }}>
        <BankValueCard label={t('bankName')} value={displayValue(data?.bankName)} mainColor={MAIN_COLOR} secondaryColor={SECONDORY_COLOR} />
        <BankValueCard label={t('bankCode')} value={displayValue(data?.bankCode)} mainColor={MAIN_COLOR} secondaryColor={SECONDORY_COLOR} />
        <BankValueCard label={t('bankAccount')} value={displayValue(data?.bankAccount)} mainColor={MAIN_COLOR} secondaryColor={SECONDORY_COLOR} />
        {data?.bankAccountType ?
          <BankValueCard label={t('bankAccountType', { defaultValue: 'Tipo de cuenta' })} value={displayValue(data?.bankAccountType)} mainColor={MAIN_COLOR} secondaryColor={SECONDORY_COLOR} />
          : null}
      </Grid>
    </Card>
  );
};
